'use client';

import dayjs from 'dayjs';
import { createContext, useContext } from 'react';
import { useStore } from 'zustand/react';
import { createStore, type StoreApi } from 'zustand/vanilla';
import type { FormattedVenueOpeningHour } from '@/entities/venue/lib/get-formatted-venue-opening-hours.util';
import {
    EMPTY_VENUE_AVAILABILITY_STATUS,
    areCurrentOutagesEqual,
    areVenueAvailabilityStatusesEqual,
    computeVenueAvailabilitySnapshot,
    type OutageWithTimeAvailability,
    type VenueAvailabilityStatus,
} from '@/entities/venue/lib/venue-availability.utils';

export type VenueAvailabilitySnapshot = {
    /** Shared clock for status, outage filtering, and the recompute timer. */
    computedAtMs: number;
    currentOutages: OutageWithTimeAvailability[];
    allOutages: OutageWithTimeAvailability[];
    status: VenueAvailabilityStatus;
    /**
     * False until the first answer is known. Do not infer readiness from
     * `status.kind` — `ClosedUnknown` is a real closed state, not "loading".
     */
    hasComputed: boolean;
    nextRecomputeAtMs: number | null;
    formattedOpeningHours: FormattedVenueOpeningHour[];
};

export type VenueAvailabilityRecomputeInput = {
    computedAtMs: number;
    outages: OutageWithTimeAvailability[] | undefined;
    formattedOpeningHours: FormattedVenueOpeningHour[];
};

export type VenueAvailabilityStore = VenueAvailabilitySnapshot & {
    recompute: (input: VenueAvailabilityRecomputeInput) => void;
};

export type VenueAvailabilityStoreApi = StoreApi<VenueAvailabilityStore>;

export const EMPTY_VENUE_AVAILABILITY_SNAPSHOT: VenueAvailabilitySnapshot = {
    computedAtMs: 0,
    currentOutages: [],
    allOutages: [],
    status: EMPTY_VENUE_AVAILABILITY_STATUS,
    hasComputed: false,
    nextRecomputeAtMs: null,
    formattedOpeningHours: [],
};

export const VenueAvailabilityStoreContext = createContext<VenueAvailabilityStoreApi | null>(null);

/** Pass `initial` so SSR `getInitialState()` matches the seeded snapshot. */
export const createVenueAvailabilityStore = (
    initial?: Partial<VenueAvailabilitySnapshot>,
): VenueAvailabilityStoreApi =>
    createStore<VenueAvailabilityStore>((set, get) => ({
        ...EMPTY_VENUE_AVAILABILITY_SNAPSHOT,
        computedAtMs: dayjs().valueOf(),
        ...initial,
        recompute: ({ computedAtMs, outages, formattedOpeningHours }) => {
            const next = computeVenueAvailabilitySnapshot({
                computedAtMs,
                outages,
                formattedOpeningHours,
            });
            const prev = get();

            const status = areVenueAvailabilityStatusesEqual(prev.status, next.status)
                ? prev.status
                : next.status;
            const currentOutages = areCurrentOutagesEqual(prev.currentOutages, next.currentOutages)
                ? prev.currentOutages
                : next.currentOutages;
            const allOutages =
                prev.allOutages.length === next.allOutages.length &&
                prev.allOutages.every((o, i) => o.id === next.allOutages[i]?.id)
                    ? prev.allOutages
                    : next.allOutages;
            const hours =
                prev.formattedOpeningHours === next.formattedOpeningHours
                    ? prev.formattedOpeningHours
                    : next.formattedOpeningHours;

            if (
                prev.hasComputed &&
                status === prev.status &&
                currentOutages === prev.currentOutages &&
                allOutages === prev.allOutages &&
                hours === prev.formattedOpeningHours &&
                next.nextRecomputeAtMs === prev.nextRecomputeAtMs &&
                computedAtMs === prev.computedAtMs
            ) {
                return;
            }

            set({
                computedAtMs,
                currentOutages,
                allOutages,
                status,
                hasComputed: true,
                nextRecomputeAtMs: next.nextRecomputeAtMs,
                formattedOpeningHours: hours,
            });
        },
    }));

export function useVenueAvailabilityStore<T>(selector: (state: VenueAvailabilityStore) => T): T {
    const store = useContext(VenueAvailabilityStoreContext);
    if (!store) {
        throw new Error('useVenueAvailabilityStore must be used within VenueAvailabilityProvider');
    }
    return useStore(store, selector);
}
